import { useEffect, useState } from 'react';
import { loadRemoteModule, type RemoteModuleExport } from '@/lib/remoteLoader';
import { REMOTE_REGISTRY } from '@/lib/remoteRegistry';
import { useCapabilities } from '@/hooks/useCapabilities';

/**
 * Story 123.2 — fetches the remote module for a capability key if (a)
 * the tenant has that capability effective AND (b) the registry has
 * an entry for it. Returns:
 *
 *   - `module`  : the loaded [RemoteModuleExport] once fetched.
 *   - `loading` : true while the fetch is in flight.
 *   - `error`   : non-null when the load failed (network / parse).
 *   - `denied`  : true when the tenant lacks the capability — the
 *                 shell renders a "module not available" or nothing.
 */
export function useRemoteModule(capabilityKey: string) {
  const { has, loading: capsLoading } = useCapabilities();
  const [module, setModule] = useState<RemoteModuleExport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const entry = REMOTE_REGISTRY[capabilityKey];
  const granted = has(capabilityKey);

  useEffect(() => {
    if (capsLoading) return;
    if (!granted || !entry) {
      setModule(null);
      return;
    }
    setLoading(true);
    setError(null);
    loadRemoteModule(entry.url)
      .then((m) => setModule(m))
      .catch((e) => setError(e instanceof Error ? e : new Error(String(e))))
      .finally(() => setLoading(false));
  }, [capabilityKey, entry, granted, capsLoading]);

  return {
    module,
    loading,
    error,
    denied: !capsLoading && !granted,
    hasEntry: !!entry,
  };
}
